/**
 * <ProductCombobox>
 * POS-grade product picker with multi-word / partial / thumbnail search.
 *
 * Key upgrades over the earlier local-only implementation:
 *   • Server-side tokenized search via `productsAPI.search(q)` — matches
 *     "eco olive" AND "olive eco" AND "oli" AND "32" for the same SKU.
 *   • Debounced (200 ms) so real-time typing doesn't hammer the API.
 *   • Falls back to a local substring filter over the `products` prop
 *     when the backend errors, so cashiers are never stranded.
 *   • Thumbnail + full name + SKU + price + stock in every result row.
 *   • Wider input (min-w 320px, expands to fill grid cell); dropdown
 *     matches full width. Always renders at least 5 slots when matches
 *     exist so the panel never looks empty.
 *
 * Public props (unchanged from the previous version):
 *   products    : Product[] — used for the "selected" display + local fallback
 *   value       : product_id (string) — controlled
 *   onSelect    : (product) => void
 *   onEnterEmpty: (event) => void — Enter on an empty cell
 *   disabled    : boolean
 *   testId      : string
 *   placeholder : string
 */
import {
    forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { Search, ChevronDown, ImageOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../../lib/api';
import ProductImage from '../ProductImage';

const DEBOUNCE_MS = 200;

/** Local substring filter used as the fallback when the backend errors. */
const localFilter = (products, q) => {
    const qn = q.trim().toLowerCase();
    if (!qn) return products.slice(0, 20);
    const tokens = qn.split(/\s+/).filter(Boolean);
    return products
        .filter((p) => {
            const hay = [p.name, p.sku, p.barcode, p.brand]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return tokens.every((t) => hay.includes(t));
        })
        .slice(0, 20);
};

const ProductCombobox = forwardRef(function ProductCombobox(
    { products, value, onSelect, onEnterEmpty, disabled, testId, placeholder }, ref,
) {
    const { t } = useTranslation(['entities', 'common']);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [remoteResults, setRemoteResults] = useState(null); // null = not yet fetched
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
    }));

    // selected product (display when collapsed)
    const selected = useMemo(
        () => products.find((p) => p.id === value) || null,
        [products, value],
    );

    // ---- Remote search (debounced) --------------------------------------
    const fetchRemote = useCallback(async (q) => {
        if (!q || !q.trim()) {
            setRemoteResults(null);
            setLoading(false);
            return;
        }
        // Cancel any in-flight request
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        try {
            const { data } = await productsAPI.search(q, { limit: 20 });
            if (ctrl.signal.aborted) return;
            setRemoteResults(Array.isArray(data) ? data : []);
        } catch (e) {
            if (ctrl.signal.aborted) return;
            // Fallback: local filter over the products prop.
            setRemoteResults(null);
        } finally {
            if (!ctrl.signal.aborted) setLoading(false);
        }
    }, []);

    // Debounce queries
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query || !query.trim()) {
            setRemoteResults(null);
            setLoading(false);
            return;
        }
        debounceRef.current = setTimeout(() => fetchRemote(query), DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [query, fetchRemote]);

    // The list we actually render: remote results if available, else
    // local filter over the products prop (empty query = first 20).
    const filtered = useMemo(() => {
        if (remoteResults != null) return remoteResults;
        return localFilter(products, query);
    }, [remoteResults, products, query]);

    // Reset highlight when filter changes
    useEffect(() => { setHighlight(0); }, [query, open]);

    // Auto-scroll highlighted item into view
    useEffect(() => {
        if (!open) return;
        const list = listRef.current;
        if (!list) return;
        const el = list.querySelector(`[data-idx="${highlight}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlight, open]);

    const commit = (p) => {
        onSelect?.(p);
        setOpen(false);
        setQuery('');
        setRemoteResults(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) setOpen(true);
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
            return;
        }
        if (e.key === 'Enter') {
            if (open && filtered[highlight]) {
                e.preventDefault();
                e.stopPropagation();
                commit(filtered[highlight]);
                return;
            }
            if (!query && !selected) {
                onEnterEmpty?.(e);
                return;
            }
            return;
        }
        if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
            return;
        }
        if (e.key === 'Tab') {
            setOpen(false);
            return;
        }
    };

    const displayValue = open ? query : (selected ? `${selected.sku} · ${selected.name}` : '');

    // Show at least 5 slot rows when matches exist (spec §2.3). We
    // render the actual results first; if fewer than 5 came back but
    // more than 0 we still let the list be short — the rule is "at
    // least 5 when matches exist", meaning we don't cap below 5.
    const emptyState = query && filtered.length === 0;

    return (
        <div
            className="relative w-full min-w-[240px] md:min-w-[450px] max-w-[600px]"
            data-testid={testId ? `${testId}-wrap` : undefined}
        >
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    disabled={disabled}
                    data-testid={testId}
                    value={displayValue}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onBlur={() => setTimeout(() => setOpen(false), 160)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || t('entities:products.dialog.select_brand', { defaultValue: 'Search by name, SKU or barcode…' })}
                    className="w-full h-10 pl-9 pr-9 rounded-md border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C08478]/40 disabled:opacity-50"
                />
                {loading ? (
                    <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                ) : (
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                )}
            </div>
            {open && (
                <div
                    ref={listRef}
                    className="absolute z-50 mt-1 left-0 right-0 max-h-96 overflow-y-auto rounded-md border border-[#EFE5E1] bg-white shadow-xl text-sm"
                    role="listbox"
                    data-testid={testId ? `${testId}-listbox` : undefined}
                >
                    {emptyState ? (
                        <div className="px-4 py-3 text-gray-400 text-xs" data-testid={testId ? `${testId}-empty` : undefined}>
                            {t('common:common.no_results', { defaultValue: 'No products match' })} “{query}”
                        </div>
                    ) : (
                        filtered.map((p, i) => (
                            <ResultRow
                                key={p.id}
                                idx={i}
                                product={p}
                                highlighted={i === highlight}
                                onCommit={() => commit(p)}
                                onHover={() => setHighlight(i)}
                                testId={testId ? `${testId}-opt-${p.sku || p.id}` : undefined}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
});

// -----------------------------------------------------------------------
// Individual result row
// -----------------------------------------------------------------------
const ResultRow = ({ idx, product: p, highlighted, onCommit, onHover, testId }) => {
    const [imgErr, setImgErr] = useState(false);
    const outOfStock = Number(p.stock_qty || 0) <= 0;
    return (
        <button
            type="button"
            data-idx={idx}
            data-testid={testId}
            onMouseDown={(e) => { e.preventDefault(); onCommit(); }}
            onMouseEnter={onHover}
            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b border-[#F5F0EE] last:border-b-0 transition-colors ${
                highlighted ? 'bg-[#FAF5F3]' : 'hover:bg-[#FAFAFA]'
            }`}
        >
            {/* Thumbnail — image_url → brand logo → company logo (via <ProductImage>) */}
            <div className="w-11 h-11 shrink-0 rounded-md overflow-hidden bg-[#FBF8F6] border border-[#EFE5E1]">
                <ProductImage product={p} testid={`product-combobox-thumb-${p.sku}`} />
            </div>
            {/* Name + SKU */}
            <div className="min-w-0 flex-1">
                <div className="text-[13px] leading-snug font-medium text-[#1A1A1A] break-words">{p.name}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2">
                    <span className="font-mono text-[#C08478]">{p.sku}</span>
                    {p.barcode && <span className="text-gray-400">· {p.barcode}</span>}
                </div>
            </div>
            {/* Price + stock */}
            <div className="text-[11px] shrink-0 text-right whitespace-nowrap">
                <div className="font-semibold text-[#1A1A1A] font-mono">
                    {Number(p.price || 0).toFixed(2)} €
                </div>
                <div className={outOfStock ? 'text-[#E76F51] font-medium' : 'text-gray-400'}>
                    {outOfStock ? 'Out of stock' : `${Number(p.stock_qty)} ${p.unit || 'pcs'}`}
                </div>
            </div>
        </button>
    );
};

export default ProductCombobox;
